import { hideProductInSelector, showProductInSelector, setVisibleProducts } from 'actions/productVisibilityActions';
import { fabric } from 'fabric';
import store from '../../store';
import localFontPreloader from '../../utils/localFontPreloader';

// Detect whether we're in buy plan mode based on buyplan info
let isBuyPlanMode = false;

// Function to detect buy plan mode from buyplan info
export function detectBuyPlanMode() {
  try {
    // Get buyplan info from Redux store
    const state = store.getState();
    const buyplanInfo = state?.get?.('buyplanInfoReducer')?.get?.('data');
    
    if (buyplanInfo) {
      // Check the state property to determine mode
      const buyplanState = buyplanInfo.get('state');
      
      isBuyPlanMode = buyplanState === 'buyplan';
      return isBuyPlanMode;
    }
    
    return false;
  } catch (e) {
    return false;
  }
}

// Expose mode detection to other components
export function isBuyPlan() {
  return isBuyPlanMode;
}

// Update mode on demand (can be called from other components)
export function updateBuyPlanMode() {
  detectBuyPlanMode();
  return isBuyPlanMode;
}

// Canonical Helpers.js - clean implementation
let fabcanvas = null;
let canvas_zoom_perc = 100;
const CUSTOM_PROPS = ['pimage', 'pname', 'skutxt', 'product_sku', 'product_id', 'productcolorcode', 'productwprice', 'productrprice', 'productsizes', 'padding'];
let mainBuyplanItems = null;
let canvasBuyplanItems = null;
let selectorBuyplanItems = null;
let SEL_MONTH = null;
let ACTIVE_PAGE_ID = '';
let IS_EXPORT_PDF = false;
let IS_PDF_EXPORT_LOADING = false; // Flag to track PDF export loading state (for background overlay)
let FITCANVASVALUE = 100; // Initialize to 100% to match default canvas zoom
let IS_FITTING_TO_CANVAS = false; // Flag to prevent sync loops during fit to canvas
let LAST_FIT_TO_CANVAS_ZOOM = null; // Store the last calculated fit-to-canvas zoom to prevent recalculation

// apply-to flags
let applyToSelector = true;
let applyToCanvas = true;

export function getApplyToSelector() { return applyToSelector; }
export function setApplyToSelector(v) { applyToSelector = !!v; }
export function getApplyToCanvas() { return applyToCanvas; }
export function setApplyToCanvas(v) { applyToCanvas = !!v; }

export function getSelMonth() { return SEL_MONTH; }
export function setSelMonth(m) { SEL_MONTH = m; }
export function getActivePageId() { return ACTIVE_PAGE_ID; }
export function setActivePageId(id) { ACTIVE_PAGE_ID = id; }
export function suspendAutoSave() { /* Auto save suspended */ }
export function resumeAutoSave() { /* Auto save resumed */ }
export function getIsExportPDF() { return IS_EXPORT_PDF; }
export function setIsExportPDF(v) { IS_EXPORT_PDF = !!v; }
export function getIsPDFExportLoading() { return IS_PDF_EXPORT_LOADING; }
export function setIsPDFExportLoading(v) { IS_PDF_EXPORT_LOADING = !!v; }

// Helper function to create ID mappings based on the current mode
export function createIdMappings() {
  const inBuyPlanMode = detectBuyPlanMode();
  const idMap = new Map();
  
  if (!window.buyplanSummaryData) {
    return idMap;
  }
  
  try {
    const buyplanItems = window.buyplanSummaryData.get('buyplan_items');
    if (buyplanItems) {
      for (const [, monthData] of buyplanItems.entries()) {
        const items = monthData.get('items');
        if (items) {
          items.forEach(item => {
            const buyplanItemId = String(item.get('id'));
            const productId = String(item.get('product_id'));
            
            // Always map buyplan item ID to product ID (this is always needed)
            idMap.set(buyplanItemId, productId);
            
            // Only map product ID to buyplan item ID in buy plan mode
            if (inBuyPlanMode) {
              idMap.set(productId, buyplanItemId);
            }
          });
        }
      }
    }
  } catch (e) {
    // Silently ignore mapping errors
  }
  
  return idMap;
}

export function getMainBuyplanItems() { return mainBuyplanItems; }
export function setMainBuyplanItems(items) { mainBuyplanItems = items; }
export function getCanvasBuyplanItems() { return canvasBuyplanItems; }
export function setCanvasBuyplanItems(items) { canvasBuyplanItems = items; }
export function getSelectorBuyplanItems() { return selectorBuyplanItems; }
export function setSelectorBuyplanItems(items) { selectorBuyplanItems = items; }
export function getCustomProps() { return CUSTOM_PROPS; }

export function getCanvas() { return fabcanvas; }
export function setCanvas(c) { if (c) fabcanvas = c; }

export function getLabelState() {
	try {
		const state = store.getState();
		const labelState = state && (state.get ? state.get('labelReducer') : state.labelReducer);
		return {
			product_name: labelState?.get ? labelState.get('product_name') : (labelState?.product_name || false),
			product_sku: labelState?.get ? labelState.get('product_sku') : (labelState?.product_sku || false),
			product_color: labelState?.get ? labelState.get('product_color') : (labelState?.product_color || false),
			product_wprice: labelState?.get ? labelState.get('product_wprice') : (labelState?.product_wprice || false),
			product_rprice: labelState?.get ? labelState.get('product_rprice') : (labelState?.product_rprice || false),
			product_sizes: labelState?.get ? labelState.get('product_sizes') : (labelState?.product_sizes || false),
		};
	} catch (e) {
		/* ignore */
		return { product_name: false, product_sku: false, product_color: false, product_wprice: false, product_rprice: false, product_sizes: false };
	}
}

export function addGroupedImage(pimage, options = {}, pname = '', skutxt = '', productcolorcode = '', productwprice = '', productrprice = '', productsizes = '', productIdOrOpacity = '', opacityArg = undefined) {
	const canvas = getCanvas();
	if (!canvas) return;
	// Get current canvas scale percentage (defaults to 100% if not available)
	const currentScalePercent = (window.__getCurrentCanvasScale && window.__getCurrentCanvasScale()) || 100;
	const scaleFactor = currentScalePercent / 100; // Convert percentage to scale factor (e.g., 25% = 0.25)
	// normalize backward-compatible params: callers used to pass opacity as the 9th arg.
	let buyplanItemId = '';
	let opacity = 1;
	if (typeof opacityArg === 'undefined') {
		// Only 9th param provided (or none). If it's a small number <=1 treat it as opacity, else it's productId.
		if (typeof productIdOrOpacity === 'number' && productIdOrOpacity <= 1) {
			opacity = productIdOrOpacity;
			buyplanItemId = '';
		} else {
			buyplanItemId = productIdOrOpacity || '';
			opacity = 1;
		}
	} else {
		// Both provided: 9th is productId, 10th is opacity
		buyplanItemId = productIdOrOpacity || '';
		opacity = opacityArg;
	}

	// CRITICAL: Always ensure we have a valid product_id for canvas persistence
	let actualProductId = '';
	
	// Try to look up actual product_id from buyplan data
	if (buyplanItemId && window.buyplanSummaryData) {
		try {
			const buyplanItems = window.buyplanSummaryData.get('buyplan_items');
			if (buyplanItems) {
				// Search through all months including SUGGESTED ITEMS
				for (const [, monthData] of buyplanItems.entries()) {
					const items = monthData.get('items');
					if (items) {
						const item = items.find(item => 
							item.get('id') === parseInt(buyplanItemId)
						);
						if (item) {
							actualProductId = item.get('product_id');
							break;
						}
					}
				}
			}
		} catch (e) {
			// If lookup fails, fall back to using buyplanItemId
			actualProductId = buyplanItemId;
		}
	}
	
	// CRITICAL: Always ensure we have a product_id - this prevents canvas objects from becoming invalid
	if (!actualProductId) {
		actualProductId = String(buyplanItemId || 'unknown');
	}

	// Store the actual SKU text before we overwrite skutxt with buyplanItemId
	const actualSkuText = skutxt || '';

	fabric.Image.fromURL(pimage, function (img) {
		img.scaleToHeight(80);
		const labelState = getLabelState();
		let product_text = '';
		if (labelState.product_name) product_text = pname || '';
		if (labelState.product_sku) { if (product_text) product_text += '\n'; product_text += 'SKU: ' + (actualSkuText || ''); }
		if (labelState.product_color) { if (product_text) product_text += '\n'; product_text += productcolorcode || ''; }
		if (labelState.product_wprice) { if (product_text) product_text += '\n'; product_text += productwprice || ''; }
		if (labelState.product_rprice) { if (product_text) product_text += '\n'; product_text += productrprice || ''; }
		if (labelState.product_sizes) { if (product_text) product_text += '\n'; product_text += 'SIZE: ' + (productsizes || ''); }

	img.set({ 
		originX: 'center', 
		originY: 'top', 
		left: 0, 
		top: 0,
		stroke: null, // Explicitly remove stroke to prevent borders in PDF export
		strokeWidth: 0
	});
	const imgScaledWidth = img.getScaledWidth();
	const imgScaledHeight = img.getScaledHeight();
	// Count the number of lines in the text to adjust height properly
	const lineCount = (product_text.match(/\n/g) || []).length + 1;
	
	// Calculate text height based on line count and font size
	// Use a more generous calculation to ensure all text fits
	const calculatedHeight = lineCount * 12; // 12px per line instead of 9px
	
	// Add padding between image and labels
	const imageLabelPadding = 5;
	
	// Create text box with proper spacing for labels
	const producttext = new fabric.Textbox((product_text || '').toUpperCase(), {
		left: 0,
		top: imgScaledHeight + imageLabelPadding, // Add padding between image and labels
		originX: 'center',
		originY: 'top',
		width: imgScaledWidth,
		height: calculatedHeight, // Height based on actual content
		fontSize: 7,
		fontFamily: 'Roboto',
		textAlign: 'center',
		fill: '#000000',
		fontWeight: 500,
		lineHeight: 1.2, // Proper line height for text
		padding: 2 // Small padding to prevent text cropping
	});

const group = new fabric.Group([img, producttext], { 
	originX: 'center', 
	originY: 'top',
	stroke: null, // Explicitly remove stroke to prevent borders in PDF export
	strokeWidth: 0
});
		// Store the desired position before scaling
		let targetLeft = options.left;
		let targetTop = options.top;
		
		if (options.left !== undefined && options.top !== undefined) {
			// Set initial position
			group.left = targetLeft;
			group.top = targetTop;
			group.setCoords();
			if (options.scaleX) { group.scaleX = options.scaleX; group.scaleY = options.scaleY; }
		} else if (canvas) {
			targetLeft = canvas.width / 2;
			targetTop = canvas.height / 2;
			group.left = targetLeft;
			group.top = targetTop;
			group.setCoords();
		}
	group.setControlsVisibility({ mt: false, mb: false, ml: false, mr: false });
	// attach custom properties used by save/restore and API payloads
	group.pimage = pimage;
	group.pname = pname;
	group.skutxt = buyplanItemId; // Store buyplan item ID for product identification
	group.product_sku = actualSkuText; // Store the actual SKU text (e.g., "FT-03") separately for labels
	group.product_id = actualProductId;
	group.productcolorcode = productcolorcode;
	group.productwprice = productwprice;
	group.productrprice = productrprice;
	group.productsizes = productsizes;
	group.opacity = opacity;
		canvas.add(group);
		// Scale product to match current canvas scale (e.g., if canvas is at 25%, scale product to 25% of base size)
		// Store current position before scaling
		const currentLeft = group.left;
		const currentTop = group.top;
		group.scaleToWidth(100 * scaleFactor);
		// Restore position after scaling (scaleToWidth might shift position due to origin point)
		group.set({ left: targetLeft !== undefined ? targetLeft : currentLeft, top: targetTop !== undefined ? targetTop : currentTop });
		group.setCoords();
		
		// CRITICAL: Final boundary check - ensure product stays within canvas bounds
		// After scaling, we know the actual product height, so adjust if needed
		const productHeight = group.getScaledHeight();
		const productWidth = group.getScaledWidth();
		let finalLeft = group.left;
		let finalTop = group.top;
		
		// Clamp horizontal position
		finalLeft = Math.max(productWidth / 2, Math.min(finalLeft, canvas.width - productWidth / 2));
		
		// If product would overflow bottom, align bottom with canvas bottom
		// Product uses originY: 'top', so top + height = bottom
		if (finalTop + productHeight > canvas.height) {
			// Align product's bottom with canvas bottom
			finalTop = Math.max(0, canvas.height - productHeight);
		}
		// Also ensure top doesn't go above canvas
		finalTop = Math.max(0, Math.min(finalTop, canvas.height));
		
		// Apply final position if it changed
		if (finalLeft !== group.left || finalTop !== group.top) {
			group.set({ left: finalLeft, top: finalTop });
			group.setCoords();
		}
		
		canvas.setActiveObject(group);
		canvas.renderAll();
		
		// Update productsEverOnCanvas before saving state
		if (buyplanItemId) {
			productsEverOnCanvas.add(String(buyplanItemId));
		}
		
		// Save state only after the image is fully loaded and added to canvas
		saveCanvasState();
		// Update selector visibility so product disappears from selector when on canvas
		// This fixes templates (buy plans use forceSyncProductVisibilityForBuyPlan from addImage path)
		// Must run after product is added - works for both drag-drop and addImage flows
		try {
			if (getApplyToSelector && getApplyToSelector()) {
				updateSelectionCanvasItems(true);
			}
		} catch (e) {
			return e
		}
	}, { crossOrigin: 'anonymous' });
}

export function addImage(pimage, options, pname, skutxt, productcolorcode, productwprice, productrprice, productsizes, productId, opacity) {
	// forward productId and optional opacity to addGroupedImage (backwards compatible)
	addGroupedImage(pimage, options, pname, skutxt, productcolorcode, productwprice, productrprice, productsizes, productId, opacity);
	
	// Note: saveCanvasState() is now called inside addGroupedImage after the image is fully loaded
	
	// Make sure we hide the product in the selector
	if (getApplyToSelector && getApplyToSelector()) {
		// productId parameter is the buyplan item ID
		const buyplanItemId = productId || '';
		
		// Hide by buyplan item ID (for product identification)
		if (buyplanItemId) {
			store.dispatch(hideProductInSelector(buyplanItemId));
		}
		// Also hide by SKU for backward compatibility
		if (skutxt && skutxt !== buyplanItemId) {
			store.dispatch(hideProductInSelector(skutxt));
		}
		
		// CRITICAL: Always try to hide by both IDs (both buyplan item ID and product ID)
		// This ensures consistent behavior regardless of mode
		try {
		    const inBuyPlanMode = detectBuyPlanMode();
		    
		    // Create a direct lookup function to find product data
		    const findProductData = (idToFind) => {
		        if (!window.buyplanSummaryData) return null;
		        
		        try {
		            const buyplanItems = window.buyplanSummaryData.get('buyplan_items');
		            if (!buyplanItems) return null;
		            
		            // Search through all months
		            for (const [, monthData] of buyplanItems.entries()) {
		                const items = monthData.get('items');
		                if (!items) continue;
		                
		                // Look for item by ID first (buyplan item ID)
		                let foundItem = items.find(item =>
		                    String(item.get('id')) === String(idToFind)
		                );
		                
		                // If not found by ID, try looking by product_id (in buy plan mode)
		                if (!foundItem && inBuyPlanMode) {
		                    foundItem = items.find(item =>
		                        String(item.get('product_id')) === String(idToFind)
		                    );
		                }
		                
		                if (foundItem) return foundItem;
		            }
		        } catch (e) {
		            return null;
		        }
		        
		        return null;
		    };
		    
		    // Find product data using buyplanItemId (for product identification)
		    const productData = findProductData(buyplanItemId);
		    
		    if (productData) {
		        // Hide by buyplan item ID
		        const buyplanItemId = String(productData.get('id'));
		        if (buyplanItemId && buyplanItemId !== skutxt) {
		            store.dispatch(hideProductInSelector(buyplanItemId));
		        }
		        
		        // Hide by product ID (always do this regardless of mode)
		        const productId = String(productData.get('product_id'));
		        if (productId) {
		            store.dispatch(hideProductInSelector(productId));
		            
		            // Try to hide any DOM element directly
		            try {
		                const elem = document.getElementById(productId);
		                if (elem) elem.style.display = 'none';
		            } catch (e) {
		                // Ignore DOM manipulation errors
		            }
		        }
		    }
		} catch (e) {
		    // Ignore errors when hiding by product ID
		}
	}
	
	// Call API to refresh canvas state all products
	callCanvasStateAllAPI();
	
	// Force synchronize product visibility in buy plan mode
	// Delay execution slightly to ensure all state is updated
	setTimeout(() => {
		forceSyncProductVisibilityForBuyPlan();
	}, 500);
}

// Function to call canvas state all API
export function callCanvasStateAllAPI() {
	try {
		// API call removed to prevent main canvas re-renders
		// const reduxState = store.getState();
		// const buyplanId = reduxState.get('buyplanIdReducer')?.get('data')?.get('id');
		// const month = getSelMonth();
		// if (buyplanId && month) {
		// 	store.dispatch(getCanvasStateAll(buyplanId, month));
		// }
	} catch (e) {
		// Ignore errors
	}
}

// Special function specifically for buy plan mode to force visibility synchronization
export function forceSyncProductVisibilityForBuyPlan() {
	try {
		// Only run in buy plan mode
		const isBuyPlanMode = detectBuyPlanMode();

		if (!isBuyPlanMode) {
			return false;
		}
		
		// Get all canvas objects
		const canvas = getCanvas();
		if (!canvas) {
			return false;
		}
		
		// Create a complete mapping of all IDs
		const idMap = new Map();
		const productsOnCanvas = new Set();
		
		// Build ID mappings from buyplan summary data
		if (window.buyplanSummaryData) {
			try {
				const buyplanItems = window.buyplanSummaryData.get('buyplan_items');
				if (buyplanItems) {
					
					// Get all months' products (including current month)
					for (const entry of buyplanItems.entries()) {
						const monthData = entry[1];
						
						const items = monthData.get('items');
						if (items && items.size > 0) {
							items.forEach(item => {
								const buyplanItemId = String(item.get('id'));
								const productId = String(item.get('product_id'));
								
								
								// Store bidirectional mappings
								idMap.set(buyplanItemId, productId);
								idMap.set(productId, buyplanItemId);
							});
						}
					}
				}
			} catch (e) {
				// Ignore mapping errors
			}
		}
		
		// Collect all product IDs from canvas objects (both skutxt and product_id)
		const objects = canvas.getObjects();
		objects.forEach(obj => {
			if (!obj) return;
			
			if (obj.skutxt) {
				const skutxt = String(obj.skutxt);
				productsOnCanvas.add(skutxt);
				
				// Add the mapped ID as well
				if (idMap.has(skutxt)) {
					const mappedId = idMap.get(skutxt);
					productsOnCanvas.add(mappedId);
				}
			}
			
			if (obj.product_id) {
				const productId = String(obj.product_id);
				productsOnCanvas.add(productId);
				
				// Add the mapped ID as well
				if (idMap.has(productId)) {
					const mappedId = idMap.get(productId);
					productsOnCanvas.add(mappedId);
				}
			}
		});
		
		// Hide products directly in DOM first (immediate feedback)
		
		productsOnCanvas.forEach(id => {
			try {
				// Direct DOM manipulation for immediate feedback
				document.querySelectorAll(`[id="${id}"]`).forEach(el => {
					el.style.display = 'none';
				});
				
				// Also try data attributes
				document.querySelectorAll(`[data-product-id="${id}"]`).forEach(el => {
					el.style.display = 'none';
				});
				
				document.querySelectorAll(`[data-sku="${id}"]`).forEach(el => {
					el.style.display = 'none';
				});
			} catch (e) {
				// Error hiding DOM element
			}
		});
		
		// Update Redux store state
		try {
			
			// Get all products from summary API
			const allProductIds = [];
			if (window.buyplanSummaryData) {
				const buyplanItems = window.buyplanSummaryData.get('buyplan_items');
				if (buyplanItems) {
					for (const [, monthData] of buyplanItems.entries()) {
						const items = monthData.get('items');
						if (items && items.size > 0) {
							items.forEach(item => {
								const buyplanItemId = String(item.get('id'));
								const productId = String(item.get('product_id'));
								
								allProductIds.push(buyplanItemId);
								allProductIds.push(productId);
							});
						}
					}
				}
			}
			
			// Filter out products on canvas
			const uniqueProductIds = [...new Set(allProductIds)];
			const visibleProductIds = uniqueProductIds.filter(id => !productsOnCanvas.has(id));
			
			
			// Update Redux store in a single action
			store.dispatch(setVisibleProducts(visibleProductIds));
			
			// Force a ProductGroup component re-render
			triggerProductGroupRerender();
		} catch (e) {
			return e		
		}
		
		return true;
	} catch (e) {
		return false;
	}
}

// Global variable to force ProductGroup re-render
let productGroupRerenderTrigger = 0;

// Global variable to track products that have been on canvas (for undo/redo)
let productsEverOnCanvas = new Set();

// Flag to track when we're in an undo/redo operation
let isUndoRedoOperation = false;

// No longer needed - removed for cleaner code

// Function to trigger ProductGroup re-render (debounced to prevent excessive calls)
let rerenderTimeout = null;
export function triggerProductGroupRerender() {
	// Debounce the trigger to prevent excessive re-renders
	if (rerenderTimeout) {
		clearTimeout(rerenderTimeout);
	}
	
	rerenderTimeout = setTimeout(() => {
		productGroupRerenderTrigger++;
		rerenderTimeout = null;
	}, 150); // Increased from 50ms to 150ms for better stability
}

// Function to get the current trigger value
export function getProductGroupRerenderTrigger() {
	return productGroupRerenderTrigger;
}

export function updateSelectionCanvasItems(forceUpdate = false, forceKeepVisible = false) {
	if (!fabcanvas) return false;

	try {
		fabcanvas.discardActiveObject().renderAll();
		
		// Get all products on canvas
		const objects = fabcanvas.getObjects().filter(function (o) {
			return o.skutxt !== '';
		});

		// Count instances of each product on canvas (needed for selector visibility)
		const productCounts = {};
		objects.forEach(object => {
			// Use buyplan_item_id for identification if available, otherwise use skutxt (for backward compatibility)
			const identifier = object.buyplan_item_id || object.skutxt;
			if (identifier) {
				const id = String(identifier);
				productCounts[id] = (productCounts[id] || 0) + 1;
				// Track products that have been on canvas
				productsEverOnCanvas.add(id);
			}
		});
		
		// Build the set of product identifiers that ARE in the current filtered buyplan (for current month)
		const buyplanItems = getCanvasBuyplanItems();
		const productIdsAllowedSet = new Set(); // buyplan item id and product_id for matching
		const productSkusAllowedSet = new Set(); // sku values for matching older canvas objects
		if (buyplanItems) {
			const normalizeMonthForComparison = (monthStr) => {
				if (!monthStr) return '';
				return String(monthStr).toUpperCase().replace(/\s+/g, '_');
			};
			const selectedMonth = normalizeMonthForComparison(getSelMonth());
			// Iterate using entrySeq() for Immutable Map (same pattern as ProductGroup/canvasInfoSaga)
			const iterateMonthGroups = (fn) => {
				if (typeof buyplanItems.entrySeq === 'function') {
					buyplanItems.entrySeq().forEach(([, buyplanItem]) => fn(buyplanItem));
				} else if (typeof buyplanItems.values === 'function') {
					buyplanItems.values().forEach(fn);
				} else {
					Object.values(buyplanItems).forEach(fn);
				}
			};
			iterateMonthGroups((buyplanItem) => {
				const shortMonth = buyplanItem.get ? buyplanItem.get("short_month") : buyplanItem.short_month;
				const normalizedShortMonth = normalizeMonthForComparison(shortMonth);
				if (normalizedShortMonth === selectedMonth) {
					const items = buyplanItem.get ? buyplanItem.get("items") : buyplanItem.items;
					if (items && typeof items.forEach === 'function') {
						items.forEach((item) => {
							const get = item.get ? (k) => item.get(k) : (k) => item[k];
							const id = String(get("id"));
							productIdsAllowedSet.add(id);
							const productId = get("product_id");
							if (productId !== null && productId !== undefined && productId !== '') {
								productIdsAllowedSet.add(String(productId));
							}
							const sku = get("sku");
							if (sku !== null && sku !== undefined && sku !== '') {
								productSkusAllowedSet.add(String(sku));
							}
						});
					}
				}
			});
		}

		// Update visibility state for each object based on "Apply To Canvas" setting
		for (let i = 0; i < objects.length; i++) {
			const object = objects[i];
			const identifier = object.buyplan_item_id || object.skutxt;
			const productId = object.product_id ? String(object.product_id) : null;
			const sku = object.product_sku ? String(object.product_sku) : null;
			if (identifier || productId || sku) {
				if (getApplyToCanvas()) {
					const currentMonth = getSelMonth();
					const isSuggestedItemsMonth = currentMonth && (
						String(currentMonth).toUpperCase() === 'SUGGESTED ITEMS' ||
						String(currentMonth).toUpperCase() === 'SUGGESTED_ITEMS'
					);
					// Product is allowed if any of its identifiers (buyplan item id, product_id, sku)
					// match the filtered sets. This makes the logic robust for both old and new canvases.
					const idString = identifier ? String(identifier) : null;
					const isInFilteredSet =
						(idString && (productIdsAllowedSet.has(idString) || productSkusAllowedSet.has(idString))) ||
						(productId && productIdsAllowedSet.has(productId)) ||
						(sku && productSkusAllowedSet.has(sku));
					const shouldBeHiddenOnCanvas = !forceKeepVisible &&
						!isSuggestedItemsMonth &&
						!isInFilteredSet &&
						getApplyToCanvas();

					object.opacity = shouldBeHiddenOnCanvas ? 0 : 1;
					object.selectable = !shouldBeHiddenOnCanvas;
					object.visible = !shouldBeHiddenOnCanvas;
				} else {
					object.opacity = 1;
					object.selectable = true;
					object.visible = true;
				}
			}
		}
		
		// CRITICAL: Final check - ensure all non-product elements remain visible after product processing
		// This fixes the issue where non-product elements disappear when "Apply To Canvas" is selected
		const finalAllObjects = fabcanvas.getObjects();
		finalAllObjects.forEach(obj => {
			if (obj && !obj.skutxt) {
				// This is a non-product element (rect, arrow, text, line, etc.)
				// Ensure it's always visible regardless of "Apply To Canvas" setting
				obj.opacity = 1;
				obj.visible = true;
				obj.selectable = true;
				if (obj.setCoords) obj.setCoords();
			}
		});
		
		// Force canvas to repaint so visibility changes show immediately
		fabcanvas.requestRenderAll();
		
		// Update Redux state for selector visibility
		// Get all unique product SKUs that are currently on canvas
		const skusOnCanvas = Object.keys(productCounts);
		
		if (getApplyToSelector()) {
			// Get ALL products that should be available in the selector
			const allSelectorProductIds = [];
			
			// When Apply to Selector is checked, use filtered data (getCanvasBuyplanItems)
			const buyplanItemsForSelector = getCanvasBuyplanItems();
			if (buyplanItemsForSelector) {
				const iterateMonths = (fn) => {
					if (typeof buyplanItemsForSelector.entrySeq === 'function') {
						buyplanItemsForSelector.entrySeq().forEach(([, buyplanItem]) => fn(buyplanItem));
					} else if (typeof buyplanItemsForSelector.values === 'function') {
						buyplanItemsForSelector.values().forEach(fn);
					} else {
						Object.values(buyplanItemsForSelector).forEach(fn);
					}
				};
				iterateMonths((buyplanItem) => {
					const items = buyplanItem.get ? buyplanItem.get("items") : buyplanItem.items;
					if (items && typeof items.forEach === 'function') {
						items.forEach((item) => {
							const get = item.get ? (k) => item.get(k) : (k) => item[k];
							allSelectorProductIds.push(String(get("id")));
						});
					}
				});
			}
			
			// Also include products that were previously on canvas
			const allProductsToConsider = [...new Set([...allSelectorProductIds, ...productsEverOnCanvas])];
			
			// Create a mapping between product IDs and buyplan item IDs
			const idMap = new Map();
			const inBuyPlanMode = detectBuyPlanMode();
			
			if (window.buyplanSummaryData) {
			  try {
			    const buyplanItems = window.buyplanSummaryData.get('buyplan_items');
			    if (buyplanItems) {
			      for (let buyplanItem of buyplanItems.values()) {
			        buyplanItem.get("items").forEach((item) => {
			          const buyplanItemId = String(item.get("id"));
			          const productId = String(item.get("product_id"));
			          
			          // Always store the mapping in the forward direction
			          idMap.set(buyplanItemId, productId);
			          
			          // Only create reverse mapping in buy plan mode
			          if (inBuyPlanMode) {
			            idMap.set(productId, buyplanItemId);
			          }
			        });
			      }
			    }
			  } catch (e) {
			    // Ignore mapping errors
			  }
			}
			
			// Enhanced canvas SKUs set that includes both ID types
			const enhancedSkusOnCanvas = new Set();
			
			// Add all primary SKUs first
			skusOnCanvas.forEach(sku => {
			  enhancedSkusOnCanvas.add(sku);
			});
			
			// Add all mapped SKUs (in both directions)
			skusOnCanvas.forEach(sku => {
			  // Forward mapping
			  if (idMap.has(sku)) {
			    enhancedSkusOnCanvas.add(idMap.get(sku));
			  }
			  
			  // Reverse mapping
			  for (const [key, value] of idMap.entries()) {
			    if (value === sku) {
			      enhancedSkusOnCanvas.add(key);
			    }
			  }
			});
			
			// Batch dispatch product visibility updates - ONLY if "Apply To Selector" is checked
			const visibleProducts = allProductsToConsider.filter(id => !enhancedSkusOnCanvas.has(id));
			store.dispatch(setVisibleProducts(visibleProducts));
			
			// For initial loads or forced updates, do a more aggressive update with DOM
			// ONLY if "Apply To Selector" is checked
			if (forceUpdate) {
				skusOnCanvas.forEach(sku => {
					try {
						const elem = document.getElementById(sku);
						if (elem) elem.style.display = 'none';
						
						if (idMap.has(sku)) {
							const mappedId = idMap.get(sku);
							const mappedElem = document.getElementById(mappedId);
							if (mappedElem) mappedElem.style.display = 'none';
						}
						
						for (const [key, value] of idMap.entries()) {
							if (value === sku) {
								const reverseElem = document.getElementById(key);
								if (reverseElem) reverseElem.style.display = 'none';
							}
						}
					} catch (e) {
						// Silently fail if DOM manipulation fails
					}
				});
			}
		} else {
			// When "Apply To Selector" is unchecked, restore all products to visible
			// Use unfiltered data (main buyplan items) to get all products
			const allSelectorProductIds = [];
			const mainBuyplanItems = getMainBuyplanItems();
			
			if (mainBuyplanItems) {
				for (let buyplanItem of mainBuyplanItems.values()) {
					buyplanItem.get("items").map((item) => {
						allSelectorProductIds.push(String(item.get("id")));
					});
				}
			}
			
			// Also include products that were previously on canvas
			const allProductsToConsider = [...new Set([...allSelectorProductIds, ...productsEverOnCanvas])];
			
			// Create a mapping between product IDs and buyplan item IDs
			const idMap = new Map();
			const inBuyPlanMode = detectBuyPlanMode();
			
			if (window.buyplanSummaryData) {
			  try {
			    const buyplanItems = window.buyplanSummaryData.get('buyplan_items');
			    if (buyplanItems) {
			      for (let buyplanItem of buyplanItems.values()) {
			        buyplanItem.get("items").forEach((item) => {
			          const buyplanItemId = String(item.get("id"));
			          const productId = String(item.get("product_id"));
			          
			          // Always store the mapping in the forward direction
			          idMap.set(buyplanItemId, productId);
			          
			          // Only create reverse mapping in buy plan mode
			          if (inBuyPlanMode) {
			            idMap.set(productId, buyplanItemId);
			          }
			        });
			      }
			    }
			  } catch (e) {
			    // Ignore mapping errors
			  }
			}
			
			// Enhanced canvas SKUs set that includes both ID types
			const enhancedSkusOnCanvas = new Set();
			
			// Add all primary SKUs first
			skusOnCanvas.forEach(sku => {
			  enhancedSkusOnCanvas.add(sku);
			});
			
			// Add all mapped SKUs (in both directions)
			skusOnCanvas.forEach(sku => {
			  // Forward mapping
			  if (idMap.has(sku)) {
			    enhancedSkusOnCanvas.add(idMap.get(sku));
			  }
			  
			  // Reverse mapping
			  for (const [key, value] of idMap.entries()) {
			    if (value === sku) {
			      enhancedSkusOnCanvas.add(key);
			    }
			  }
			});
			
			// Show all products except those on canvas
			const visibleProducts = allProductsToConsider.filter(id => !enhancedSkusOnCanvas.has(id));
			store.dispatch(setVisibleProducts(visibleProducts));
			
			// Restore visibility in DOM for all products not on canvas
			if (forceUpdate) {
				allProductsToConsider.forEach(productId => {
					if (!enhancedSkusOnCanvas.has(productId)) {
						try {
							const elem = document.getElementById(productId);
							if (elem) elem.style.display = '';
							
							// Also handle mapped IDs
							if (idMap.has(productId)) {
								const mappedId = idMap.get(productId);
								const mappedElem = document.getElementById(mappedId);
								if (mappedElem) mappedElem.style.display = '';
							}
							
							for (const [key, value] of idMap.entries()) {
								if (value === productId) {
									const reverseElem = document.getElementById(key);
									if (reverseElem) reverseElem.style.display = '';
								}
							}
						} catch (e) {
							// Silently fail if DOM manipulation fails
						}
					}
				});
			}
		}

		fabcanvas.discardActiveObject().renderAll();
		
		// Trigger a re-render after a delay
		if (forceUpdate) {
			triggerProductGroupRerender();
		}
		return true;
	} catch (error) {
		return false;
	}
}

// Fix the inverted logic - this function should return true if the product exists in the array
// We no longer need global flags to track visibility

export function udpateGroupLabels() {
	const canvas = getCanvas();
	if (!canvas) return false;
	try {
		// Get active object (may be an activeSelection)
		const activeObject = canvas.getActiveObject();
		const isActiveSelection = activeObject && activeObject.type === 'activeSelection';
		
		// CRITICAL:
		// When multiple objects are selected, Fabric wraps them in an activeSelection and their
		// coordinates can become relative. If we recreate groups while the activeSelection exists,
		// we can accidentally re-apply relative coords as absolute, making objects jump to (0,0).
		//
		// Fix: snapshot selected objects, then discard activeSelection before updating labels.
		let selectedObjectsToRestore = null;
		if (isActiveSelection && activeObject._objects) {
			selectedObjectsToRestore = [...activeObject._objects];
			canvas.discardActiveObject();
			canvas.requestRenderAll();
		}
		
		const labelState = getLabelState();
		const CUSTOM_KEYS = ['pname', 'skutxt', 'product_sku', 'productcolorcode', 
		                    'productwprice', 'productrprice', 'productsizes', 'product_id', 'variant_id'];
		
		// Get all canvas objects
		const allCanvasObjects = canvas.getObjects() || [];
		
		// Create a map to track old -> new object mapping
		const objectMap = new Map();
		
		// Process each object on canvas
		for (let i = 0; i < allCanvasObjects.length; i++) {
			const obj = allCanvasObjects[i];
			
			// Skip if not a product group
			if (!obj || !obj._objects || obj._objects.length < 2 || !obj.skutxt) continue;
			
			const textObject = obj._objects[1];
			if (!textObject || textObject.type !== 'textbox') continue;

			// Build label text
			let product_text = '';
			if (labelState.product_name) product_text = obj.pname || '';
			if (labelState.product_sku) {
				if (product_text) product_text += '\n';
				const displaySku = obj.product_sku || obj.skutxt || '';
				if (displaySku) product_text += 'SKU: ' + displaySku;
			}
			if (labelState.product_color) {
				if (product_text) product_text += '\n';
				product_text += obj.productcolorcode || '';
			}
			if (labelState.product_wprice) {
				if (product_text) product_text += '\n';
				product_text += obj.productwprice || '';
			}
			if (labelState.product_rprice) {
				if (product_text) product_text += '\n';
				product_text += obj.productrprice || '';
			}
			if (labelState.product_sizes) {
				if (product_text) product_text += '\n';
				product_text += 'SIZE: ' + (obj.productsizes || '');
			}

			// Update text
			textObject.set('text', product_text.toUpperCase());
			textObject.initDimensions();

			// Preserve properties
			const preservedLeft = obj.left;
			const preservedTop = obj.top;
			const { scaleX = 1, scaleY = 1, angle = 0, originX = 'center', originY = 'top' } = obj;
			const groupObjects = obj.getObjects();

			// Remove and recreate group
			canvas.remove(obj);

			const newGroup = new fabric.Group(groupObjects, {
				left: preservedLeft,
				top: preservedTop,
				scaleX,
				scaleY,
				angle,
				originX,
				originY,
				stroke: null, // Explicitly remove stroke to prevent borders in PDF export
				strokeWidth: 0
			});

			canvas.add(newGroup);
			
			// Restore custom properties
			CUSTOM_KEYS.forEach(key => {
				if (obj[key] !== undefined) {
					newGroup[key] = obj[key];
				}
			});

			newGroup.setControlsVisibility({ mt: false, mb: false, ml: false, mr: false });
			newGroup.setCoords();
			
			// Store mapping
			objectMap.set(obj, newGroup);
		}

		// Restore activeSelection
		if (selectedObjectsToRestore && selectedObjectsToRestore.length > 0) {
			const newSelectedObjects = selectedObjectsToRestore
				.map(oldObj => objectMap.get(oldObj))
				.filter(Boolean);

			if (newSelectedObjects.length > 0) {
				newSelectedObjects.forEach(obj => obj.setCoords());
				const newActiveSelection = new fabric.ActiveSelection(newSelectedObjects, { canvas });
				newActiveSelection.setCoords();
				canvas.setActiveObject(newActiveSelection);
			}
		}

		canvas.requestRenderAll();
		saveCanvasState();
		return true;

	} catch (e) {
		return false;
	}
}


export function addText() { 
  const canvas = getCanvas(); 
  if (!canvas) return; 
  
  // Ensure offsets are recalculated before adding text (accounts for browser zoom)
  // Call it synchronously to ensure it's done before centering
  canvas.calcOffset();
  
  // Get current canvas scale percentage (defaults to 100% if not available)
  const currentScalePercent = (window.__getCurrentCanvasScale && window.__getCurrentCanvasScale()) || 100;
  const scaleFactor = currentScalePercent / 100; // Convert percentage to scale factor (e.g., 25% = 0.25)
  
  const text = new fabric.Textbox('Add Heading', { 
    fontSize: 12 * scaleFactor, // Scale font size
    width: 50 * scaleFactor, // Scale width
    fill: '#000000', 
    fontFamily: 'Roboto', 
    fontWeight: 500 
  }); 
  canvas.add(text); 
  
  // Recalculate offsets again after adding (in case adding changed something)
  canvas.calcOffset();
  
  text.center(); 
  canvas.setActiveObject(text); 
  canvas.renderAll(); 
  saveCanvasState(); 
}
export function addRect() {
  const canvas = getCanvas();
  if (!canvas) return;
  
  // Ensure offsets are recalculated before adding rect (accounts for browser zoom)
  canvas.calcOffset();
  
  // Get current canvas scale percentage (defaults to 100% if not available)
  const currentScalePercent = (window.__getCurrentCanvasScale && window.__getCurrentCanvasScale()) || 100;
  const scaleFactor = currentScalePercent / 100; // Convert percentage to scale factor (e.g., 25% = 0.25)
  
  // Use a constant stroke width so rectangle borders look the same
  // regardless of the zoom level at which they were created.
  const STROKE_WIDTH_PX = 1;
  const rect = new fabric.Rect({
    left: 100 * scaleFactor,
    top: 100 * scaleFactor,
    fill: 'transparent',
    stroke: '#000000',
    strokeWidth: STROKE_WIDTH_PX,
    strokeUniform: true,
    width: 200 * scaleFactor, // Scale width
    height: 200 * scaleFactor // Scale height
  });
  canvas.add(rect);
  
  // Recalculate offsets again after adding (in case adding changed something)
  canvas.calcOffset();
  
  rect.center();
  // Snap position to whole pixels so the new rectangle looks as
  // crisp as existing ones already on the canvas at the current zoom.
  rect.left = Math.round(rect.left || 0);
  rect.top = Math.round(rect.top || 0);
  rect.setCoords();
  rect.sendToBack();
  canvas.setActiveObject(rect);
  canvas.renderAll();
  saveCanvasState();
}

// function setCanvasWidthHeight(width, height) { const canvas = getCanvas(); if (!canvas) return; canvas.setDimensions({ width, height }); canvas.calcOffset && canvas.calcOffset(); canvas.renderAll(); }

export function resetCanvasZoom(zPerc) { 
  // Replaced zoom with dimension changes - reset to base size
  zPerc = parseInt(zPerc) || 100;
  canvas_zoom_perc = zPerc;
  // Update FITCANVASVALUE so getCurrentZoomPercent returns the correct value
  setFittoCanvas(zPerc);
  // Clear LAST_FIT_TO_CANVAS_ZOOM when resetting zoom
  LAST_FIT_TO_CANVAS_ZOOM = null;
  if (window.__updateCanvasDimensions) {
    window.__updateCanvasDimensions(zPerc);
  }
  const canvas = getCanvas();
  if (canvas) {
    canvas.setZoom(1); // Always keep zoom at 1
    canvas.discardActiveObject && canvas.discardActiveObject();
    canvas.requestRenderAll && canvas.requestRenderAll();
    saveCanvasState();
  }
}

export function fittocanvas() {
	const canvas = getCanvas();
	if (!canvas) return false;
	const canvasArea = document.querySelector('.canvasArea');
	if (!canvasArea) return false;
	
	// Prevent multiple simultaneous fit to canvas operations
	if (IS_FITTING_TO_CANVAS) {
		return false;
	}
	
	// Set flag to prevent sync loops
	IS_FITTING_TO_CANVAS = true;
	
	try {
		if (!canvas.getObjects().length) { 
			canvas_zoom_perc = 100; 
			if (window.__updateCanvasDimensions) {
				window.__updateCanvasDimensions(100);
			}
			canvas.setZoom(1); 
			setFittoCanvas(100); 
			canvas.requestRenderAll(); 
			saveCanvasState();
			// Clear flag after fit to canvas completes
			setTimeout(() => {
				IS_FITTING_TO_CANVAS = false;
			}, 100);
			return true; 
		}
		const viewportWidth = canvasArea.clientWidth; 
		const viewportHeight = canvasArea.clientHeight;
		
		// Get all objects and calculate their bounding box
		const objects = canvas.getObjects();
		if (!objects || objects.length === 0) return false;
		
		// Calculate the bounding box of all objects
		let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
		objects.forEach(obj => {
			obj.setCoords();
			const bounds = obj.getBoundingRect();
			minX = Math.min(minX, bounds.left);
			minY = Math.min(minY, bounds.top);
			maxX = Math.max(maxX, bounds.left + bounds.width);
			maxY = Math.max(maxY, bounds.top + bounds.height);
		});
		
		const contentWidth = maxX - minX;
		const contentHeight = maxY - minY;
		
		// Validate dimensions to prevent NaN
		if (!contentWidth || !contentHeight || contentWidth <= 0 || contentHeight <= 0 || 
		    !viewportWidth || !viewportHeight || viewportWidth <= 0 || viewportHeight <= 0) {
			canvas_zoom_perc = 100;
			if (window.__updateCanvasDimensions) {
				window.__updateCanvasDimensions(100);
			}
			canvas.setZoom(1);
			setFittoCanvas(100);
			canvas.requestRenderAll();
			saveCanvasState();
			// Clear flag after fit to canvas completes
			setTimeout(() => {
				IS_FITTING_TO_CANVAS = false;
			}, 100);
			return true;
		}
		
		// Calculate scale to fit content in viewport (with margin for padding)
		const margin = 10; // 10px margin on each side
		const wPerc = (viewportWidth - (margin * 2)) / contentWidth;
		const hPerc = (viewportHeight - (margin * 2)) / contentHeight;
		let zPerc = Math.min(wPerc, hPerc) * 100;
		zPerc = Math.max(10, zPerc);
		if (isNaN(zPerc) || !isFinite(zPerc)) zPerc = 100;
		
		// Get current scale and calculate scale factor
		const currentScalePercent = (window.__getCurrentCanvasScale && window.__getCurrentCanvasScale()) || 100;
		const currentScale = currentScalePercent / 100;
		const newScale = zPerc / 100;
		const scaleFactor = newScale / currentScale;
		
		// Calculate where the top-left corner of content will be after scaling
		const scaledMinX = minX * scaleFactor;
		const scaledMinY = minY * scaleFactor;
		
		// Target position: top-left with margin (not centered)
		const targetX = margin;
		const targetY = margin;
		
		// Calculate shift needed to position content at top-left with margin
		const shiftX = targetX - scaledMinX;
		const shiftY = targetY - scaledMinY;
		
		// Round the zoom to avoid floating point precision issues
		zPerc = Math.round(zPerc);
		
		// Set the zoom value immediately so it's available synchronously
		canvas_zoom_perc = zPerc;
		setFittoCanvas(zPerc); // Set immediately so getFittoCanvas() returns correct value
		LAST_FIT_TO_CANVAS_ZOOM = zPerc; // Store the calculated zoom value
		
		// Update canvas dimensions first (this will scale objects)
		if (window.__updateCanvasDimensions) {
			window.__updateCanvasDimensions(zPerc);
		}
		
		// Apply positioning shift after scaling
		// Use requestAnimationFrame to ensure updateCanvasDimensions has completed
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				// Apply shift to position all objects at top-left with margin
				objects.forEach(obj => {
					obj.left = (obj.left || 0) + shiftX;
					obj.top = (obj.top || 0) + shiftY;
					if (obj.setCoords) obj.setCoords();
				});
				
				canvas.setZoom(1); 
				canvas.discardActiveObject && canvas.discardActiveObject(); 
				canvas.requestRenderAll && canvas.requestRenderAll(); 
				
				// Ensure zoom value is still set correctly after repositioning
				canvas_zoom_perc = LAST_FIT_TO_CANVAS_ZOOM;
				setFittoCanvas(LAST_FIT_TO_CANVAS_ZOOM);
				
				saveCanvasState();
				
				// Clear flag after fit to canvas completes (longer delay to ensure all async ops finish)
				setTimeout(() => {
					IS_FITTING_TO_CANVAS = false;
				}, 800);
			});
		});
		
		return true;
	} catch (e) { 
		IS_FITTING_TO_CANVAS = false;
	}
}

export function getFittoCanvas() { return FITCANVASVALUE; }
export function setFittoCanvas(value) { 
	FITCANVASVALUE = value; 
	// Also update canvas_zoom_perc to keep them in sync
	canvas_zoom_perc = value;
	// Expose on window for FabricCanvas.js to access
	if (typeof window !== 'undefined') {
		window.setFittoCanvas = setFittoCanvas;
		window.canvas_zoom_perc = value;
	}
}
export function getCurrentZoomPercent() { 
	// Don't return values during fit to canvas to prevent intermediate values
	if (IS_FITTING_TO_CANVAS) {
		// Return the stored fit-to-canvas zoom value if available
		if (LAST_FIT_TO_CANVAS_ZOOM !== null && LAST_FIT_TO_CANVAS_ZOOM !== undefined) {
			return LAST_FIT_TO_CANVAS_ZOOM;
		}
		// Otherwise return from FITCANVASVALUE or canvas_zoom_perc
		if (FITCANVASVALUE !== null && FITCANVASVALUE !== undefined) {
			return FITCANVASVALUE;
		}
		return canvas_zoom_perc || 100;
	}
	
	// If we have a stored fit-to-canvas zoom, prefer it (prevents recalculation)
	if (LAST_FIT_TO_CANVAS_ZOOM !== null && LAST_FIT_TO_CANVAS_ZOOM !== undefined && FITCANVASVALUE === LAST_FIT_TO_CANVAS_ZOOM) {
		return LAST_FIT_TO_CANVAS_ZOOM;
	}
	
	// Try to get from FITCANVASVALUE first (set by fit to canvas)
	if (FITCANVASVALUE !== null && FITCANVASVALUE !== undefined) {
		return FITCANVASVALUE;
	}
	// Otherwise get from current canvas scale
	if (window.__getCurrentCanvasScale) {
		return window.__getCurrentCanvasScale();
	}
	// Fallback to canvas_zoom_perc
	return canvas_zoom_perc || 100;
}

export function isFittingToCanvas() {
	return IS_FITTING_TO_CANVAS;
}

export function handleZoomCanvas(value) { 
  // Replaced zoom with dimension changes - update canvas dimensions maintaining aspect ratio
  canvas_zoom_perc = value;
  // Update FITCANVASVALUE so getCurrentZoomPercent returns the correct value
  setFittoCanvas(value);
  // Clear LAST_FIT_TO_CANVAS_ZOOM when manually changing zoom (not from fit to canvas)
  LAST_FIT_TO_CANVAS_ZOOM = null;
  if (window.__updateCanvasDimensions) {
    window.__updateCanvasDimensions(value);
  }
  const canvas = getCanvas();
  if (canvas) {
    canvas.setZoom(1); // Always keep zoom at 1
    canvas.discardActiveObject && canvas.discardActiveObject();
    canvas.requestRenderAll && canvas.requestRenderAll();
    saveCanvasState();
  }
}

export function handleZoomChange(event) { 
	const canvas = getCanvas(); 
	const zValue = event && event.target && event.target.value; 
	if (zValue === 'FitToCanvas') { 
		fittocanvas(); 
		return; 
	} 
	canvas_zoom_perc = parseInt(zValue); 
	if (!canvas) return; 
	if (window.__updateCanvasDimensions) {
		window.__updateCanvasDimensions(canvas_zoom_perc);
	}
	canvas.setZoom(1); // Always keep zoom at 1
	canvas.discardActiveObject && canvas.discardActiveObject(); 
	canvas.requestRenderAll && canvas.requestRenderAll(); 
	saveCanvasState(); 
}

export function saveCanvasState() {
	if (!fabcanvas || !fabcanvas.stateaction) return;
	
	// Initialize state if it doesn't exist
	if (!fabcanvas.state) {
		fabcanvas.state = [];
		fabcanvas.index = -1;
	}

	// Before serializing, make sure all products are visible
	// This ensures that products don't disappear when saving after label changes
	const objects = fabcanvas.getObjects();

	// No need to set flags - we'll directly ensure objects are visible here
	objects.forEach((obj) => {
		if (obj && obj.skutxt) {
			// Always force products to be visible in saved state
			obj.opacity = 1;
			obj.selectable = true;
			obj.visible = true;
		}
	});
	
	const statejson = JSON.stringify(fabcanvas.toJSON(CUSTOM_PROPS));
	
	// Check if this state is different from the last saved state
	if (fabcanvas.state.length > 0) {
		const laststatejson = fabcanvas.state[fabcanvas.state.length - 1];
		if (laststatejson === statejson) {
			return false; // No change, don't save
		}
	}
	
	// If we're not at the end of the state array (i.e., we've undone some actions),
	// remove all states after the current index before adding the new one
	if (fabcanvas.index < fabcanvas.state.length - 1) {
		fabcanvas.state = fabcanvas.state.slice(0, fabcanvas.index + 1);
	}
	
	// Add the new state
	fabcanvas.state.push(statejson);
	fabcanvas.index = fabcanvas.state.length - 1;
	
	// Store productsEverOnCanvas in the canvas state for persistence across undo/redo
	// Convert Set to Array for proper serialization
	// Only update if we're not in the middle of an undo/redo operation
	if (!isUndoRedoOperation) {
		fabcanvas.productsEverOnCanvas = Array.from(productsEverOnCanvas);
	}

	// Limit the number of saved states to prevent memory issues
	if (fabcanvas.state.length > 50) {
		fabcanvas.state = fabcanvas.state.slice(-50);
		fabcanvas.index = fabcanvas.state.length - 1;
	}
	
	// Trigger a rerender of the selector canvas when state changes
	// Skip during undo/redo operations as they handle this separately
	if (!isUndoRedoOperation) {
		triggerProductGroupRerender();
	}
	
	// Defensive: ensure product_id is populated correctly when missing
	try {
		const objsForSave = (fabcanvas.getObjects && fabcanvas.getObjects()) || [];
		for (let i = 0; i < objsForSave.length; i++) {
			const obj = objsForSave[i];
			if (!obj) continue;
			
			// CRITICAL: Always ensure product_id is set for canvas persistence
			// This prevents products from disappearing on save
			if (!obj.product_id || obj.product_id === '' || obj.product_id === null) {
				let actualProductId = '';
				
				// Try to look up actual product_id from buyplan data
				if (obj.skutxt && window.buyplanSummaryData) {
					try {
						const buyplanItems = window.buyplanSummaryData.get('buyplan_items');
						if (buyplanItems) {
							// Search through all months including SUGGESTED ITEMS
							for (const [, monthData] of buyplanItems.entries()) {
								const items = monthData.get('items');
								if (items) {
									const item = items.find(item => 
										item.get('id') === parseInt(obj.skutxt)
									);
									if (item) {
										actualProductId = item.get('product_id');
										break;
									}
								}
							}
						}
					} catch (e) {
						// If lookup fails, fall back to using skutxt
						actualProductId = String(obj.skutxt);
					}
				}
				
				// CRITICAL: Always ensure we have a product_id - this prevents canvas objects from becoming invalid
				if (!actualProductId) {
					actualProductId = String(obj.skutxt || obj.id || 'unknown');
				}
				
				obj.product_id = actualProductId;
			}
		}
	} catch (e) { /* ignore */ }
}

export function undo() {
	if (!fabcanvas) return;
	
	// Initialize state if it doesn't exist
	if (!fabcanvas.state) {
		fabcanvas.state = [];
		fabcanvas.index = -1;
		return;
	}
	
	fabcanvas.stateaction = false;
	let index = fabcanvas.index || 0;
	const state = fabcanvas.state || [];
	
	// Allow undo if we have at least 1 state and we're not already at the first state
	// OR if we have only 1 state (clear the canvas completely)
	if (index > 0 || (index === 0 && state.length === 1)) {
		if (index > 0) {
			index -= 1;
		} else {
			// If we're at index 0 with only 1 state, go to empty canvas
			index = -1;
		}
		
		// Set flag to indicate we're in an undo operation
		isUndoRedoOperation = true;
		
		fabcanvas.clear();
		
		if (index >= 0 && state[index]) {
			// Wait for fonts to be ready before loading canvas state
			localFontPreloader.waitForFontsReady().then(() => {
				// Load the previous state
				fabcanvas.loadFromJSON(state[index], function () {
					fabcanvas.renderAll();
					fabcanvas.stateaction = true;
					fabcanvas.index = index;
					
					// Restore productsEverOnCanvas from canvas state
					if (fabcanvas.productsEverOnCanvas && Array.isArray(fabcanvas.productsEverOnCanvas)) {
						productsEverOnCanvas = new Set(fabcanvas.productsEverOnCanvas);
					}

					// Add small delay to ensure canvas objects are fully loaded before updating visibility
					setTimeout(() => {
						// Update selector visibility after undo
						updateSelectionCanvasItems();
						
						// Force re-render of selector canvas after Redux actions are processed
						setTimeout(() => {
							triggerProductGroupRerender();
							// Clear the undo/redo flag after operations are complete
							isUndoRedoOperation = false;
						}, 50);
					}, 100); // Give canvas time to fully load objects
				});
			});
		} else {
			// Empty canvas (no state to load)
			fabcanvas.renderAll();
			fabcanvas.stateaction = true;
			fabcanvas.index = index;
			
			// Update selector visibility after undo
			updateSelectionCanvasItems();
			
			// Force re-render of selector canvas after Redux actions are processed
			setTimeout(() => {
				triggerProductGroupRerender();
			}, 50);
		}
	} else {
		fabcanvas.stateaction = true;
	}
}

export function redo() {
	if (!fabcanvas) return;
	
	// Initialize state if it doesn't exist
	if (!fabcanvas.state) {
		fabcanvas.state = [];
		fabcanvas.index = -1;
		return;
	}
	
	fabcanvas.stateaction = false;
	let index = fabcanvas.index || 0;
	const state = fabcanvas.state || [];
	
	// Allow redo if we're not at the last state
	// Handle case when index is -1 (empty canvas) and we have states to redo to
	if (index < state.length - 1 || index === -1) {
		if (index === -1) {
			// If we're at empty canvas, go to first state (index 0)
			index = 0;
		} else {
			index += 1;
		}
		
		// Set flag to indicate we're in a redo operation
		isUndoRedoOperation = true;
		
		fabcanvas.clear();
		// Wait for fonts to be ready before loading canvas state
		localFontPreloader.waitForFontsReady().then(() => {
			fabcanvas.loadFromJSON(state[index], function () {
				fabcanvas.renderAll();
				fabcanvas.stateaction = true;
				fabcanvas.index = index;
				
				// Restore productsEverOnCanvas from canvas state
				if (fabcanvas.productsEverOnCanvas && Array.isArray(fabcanvas.productsEverOnCanvas)) {
					productsEverOnCanvas = new Set(fabcanvas.productsEverOnCanvas);
				}
				
				// Add small delay to ensure canvas objects are fully loaded before updating visibility
				setTimeout(() => {
					// Update selector visibility after redo
					updateSelectionCanvasItems();
					
					// Force re-render of selector canvas after Redux actions are processed
					setTimeout(() => {
						triggerProductGroupRerender();
						// Clear the undo/redo flag after operations are complete
						isUndoRedoOperation = false;
					}, 50);
				}, 100); // Give canvas time to fully load objects
			});
		});
	} else {
		fabcanvas.stateaction = true;
	}
}

export function resetState() {
	if (fabcanvas) {
		fabcanvas.state = [];
		fabcanvas.index = -1;
		fabcanvas.stateaction = true;
		
		// Clear the products ever on canvas tracking
		productsEverOnCanvas.clear();
		
		// Save the initial empty state so undo/redo works properly
		const initialState = JSON.stringify(fabcanvas.toJSON(CUSTOM_PROPS));
		fabcanvas.state.push(initialState);
		fabcanvas.index = 0;
	}
}

export function loadPageState(pageId, canvasState, dimensions = null) {
	if (!fabcanvas) return false;
	
	// CRITICAL: If flag is set to prevent dimension reset, don't change canvas dimensions
	// This prevents canvas from resetting during delete/clear/toggle operations
	if (window.__PREVENT_CANVAS_DIMENSION_RESET === true) {
		// Still load the canvas state, but don't reset dimensions
		if (canvasState) {
			const canvasStateForLoad = { ...canvasState };
			if (canvasStateForLoad.canvas_dimensions) {
				delete canvasStateForLoad.canvas_dimensions;
			}
			if (canvasStateForLoad.page_title) {
				delete canvasStateForLoad.page_title;
			}
			if (canvasStateForLoad.product_ids) {
				delete canvasStateForLoad.product_ids;
			}
			
			// Load canvas state without changing dimensions
			localFontPreloader.waitForFontsReady().then(() => {
				fabcanvas.loadFromJSON(canvasStateForLoad, function () {
					fabcanvas.renderAll();
				});
			});
		}
		return true; // Return early to prevent dimension changes
	}
	
	// Check if we should preserve dimensions after PDF export
	// CRITICAL: Only preserve dimensions if we're on the EXACT SAME page that was exported
	// For ANY page change, we must clear the flag and proceed with normal canvas clearing/reloading
	const shouldPreserveDimensionsAfterExport = window.__PRESERVE_CANVAS_DIMENSIONS_AFTER_EXPORT === true;
	const currentActivePageId = getActivePageId();
	
	// If flag is set but we're switching to a different page, ALWAYS clear flag and proceed normally
	// This ensures canvas is properly cleared and reloaded for the new page
	if (shouldPreserveDimensionsAfterExport && pageId !== currentActivePageId) {
		// Switching to a different page - immediately clear flag and force normal reload
		window.__PRESERVE_CANVAS_DIMENSIONS_AFTER_EXPORT = false;
		// Also reset canvas state tracking in FabricCanvas to force reload
		if (window.__resetCanvasStateTracking) {
			window.__resetCanvasStateTracking();
		}
	}
	
	// Only skip reload if flag is set AND we're on the SAME page
	if (shouldPreserveDimensionsAfterExport && pageId === currentActivePageId) {
		// After PDF export on the SAME page: preserve saved zoom (no scaling)
		setActivePageId(pageId);
		const savedZoom = dimensions?.zoom ? parseInt(dimensions.zoom) : 100;
		if (window.__updateCanvasDimensions && savedZoom) {
			window.__updateCanvasDimensions(savedZoom);
		}
		setFittoCanvas(savedZoom);
		canvas_zoom_perc = savedZoom;
		window.__PRESERVE_CANVAS_DIMENSIONS_AFTER_EXPORT = false;
		updateSelectionCanvasItems();
		return true;
	}
	
	// CRITICAL: Always clear canvas and reset state when loading a page
	// This ensures no data from previous pages persists
	setActivePageId(pageId);
	const productsOnCanvas = new Set();
	if (fabcanvas.getObjects) {
		const objects = fabcanvas.getObjects().filter(o => o.skutxt);
		objects.forEach(obj => { if (obj.skutxt) productsOnCanvas.add(obj.skutxt); });
	}
	
	// Always clear canvas - this is critical to prevent data from previous pages showing
	fabcanvas.clear();
	resetState();
	
	// Get saved zoom from dimensions parameter (page_dimensions) or default to 100
	const savedZoom = dimensions?.zoom ? parseInt(dimensions.zoom) : 100;
	
	// CRITICAL: Update FabricCanvas state tracking BEFORE loading to prevent it from reloading
	// This ensures FabricCanvas knows we're loading this state, so it won't reload and overwrite our scaled objects
	if (canvasState && window.__updateCanvasStateTracking) {
		try {
			const canvasStateForTracking = { ...canvasState };
			// Remove non-Fabric properties for tracking comparison
			if (canvasStateForTracking.canvas_dimensions) {
				delete canvasStateForTracking.canvas_dimensions;
			}
			if (canvasStateForTracking.page_title) {
				delete canvasStateForTracking.page_title;
			}
			if (canvasStateForTracking.product_ids) {
				delete canvasStateForTracking.product_ids;
			}
			const canvasStateJson = JSON.stringify(canvasStateForTracking);
			const objectsOnlyJson = JSON.stringify(canvasStateForTracking.objects || []);
			window.__updateCanvasStateTracking(canvasStateJson, objectsOnlyJson);
		} catch (e) {
			// Ignore errors updating state tracking
		}
	}
	
	// CRITICAL: On page change, always set canvas to 100% first
	// Then scale objects from their saved zoom to 100% during loadFromJSON
	// This ensures pages always render at 100% when switching, regardless of saved zoom
	// IMPORTANT: Update zoom tracking variables FIRST before updating canvas dimensions
	// This ensures getCurrentZoomPercent() returns 100% immediately, preventing UI from showing stale zoom value
	setFittoCanvas(100);
	canvas_zoom_perc = 100;
	
	// CRITICAL: Set canvas to BASE dimensions (100% zoom) BEFORE loading JSON
	// This ensures objects are loaded onto the correct canvas size
	// Base dimensions with aspect ratio 1620:940
	const BASE_WIDTH = 1620;
	const BASE_HEIGHT = 940;
	const canvas = getCanvas();
	if (canvas) {
		// Set dimensions directly first (don't use updateCanvasDimensions yet as it scales objects)
		canvas.setDimensions({
			width: BASE_WIDTH,
			height: BASE_HEIGHT
		});
		canvas.calcOffset && canvas.calcOffset();
	}
	
	// Update zoom tracking variables (but don't call updateCanvasDimensions as it scales objects)
	// We'll update dimensions tracking after loading and scaling objects
	setFittoCanvas(100);
	canvas_zoom_perc = 100;
	
	if (canvasState) {
		
		// Remove non-Fabric properties from canvasState before loading
		const canvasStateForLoad = { ...canvasState };
		if (canvasStateForLoad.canvas_dimensions) {
			delete canvasStateForLoad.canvas_dimensions;
		}
		if (canvasStateForLoad.page_title) {
			delete canvasStateForLoad.page_title;
		}
		if (canvasStateForLoad.product_ids) {
			delete canvasStateForLoad.product_ids;
		}
		// Remove width/height from canvasState JSON if present to prevent override
		if (canvasStateForLoad.width) {
			delete canvasStateForLoad.width;
		}
		if (canvasStateForLoad.height) {
			delete canvasStateForLoad.height;
		}
		
		// Wait for fonts to be ready before loading canvas state
		localFontPreloader.waitForFontsReady().then(() => {
			fabcanvas.loadFromJSON(canvasStateForLoad, function () {
				// CRITICAL: Ensure canvas dimensions are still at BASE size after load
				// This prevents JSON from overriding our dimensions
				if (canvas) {
					canvas.setDimensions({
						width: BASE_WIDTH,
						height: BASE_HEIGHT
					});
					canvas.calcOffset && canvas.calcOffset();
				}
				// CRITICAL: On page change, always scale objects to 100% size
				// This ensures objects are scaled correctly even if FabricCanvas.js doesn't run
				// Use savedZoom calculated above (from dimensions parameter)
				
				// Calculate scale factor from saved zoom
				let scaleFactor = 1;
				if (savedZoom && savedZoom !== 100) {
					scaleFactor = 100 / savedZoom;
				}
				
				// On page change: scale objects to 100% from saved zoom (e.g. 200% -> scaleFactor 0.5)
				const objects = fabcanvas.getObjects();
				
				// Scale objects if scaleFactor indicates they need scaling
				if (scaleFactor !== 1) {
					// Scale all objects
					objects.forEach(obj => {
						if (!obj) return;
						
						// Store original values before scaling
						const originalScaleX = obj.scaleX || 1;
						const originalScaleY = obj.scaleY || 1;
						const originalLeft = obj.left || 0;
						const originalTop = obj.top || 0;
						
						// Scale object dimensions (scaleX and scaleY)
						if (obj.scaleX !== undefined && obj.scaleY !== undefined) {
							obj.scaleX = originalScaleX * scaleFactor;
							obj.scaleY = originalScaleY * scaleFactor;
						}
						// Scale object position
						obj.left = originalLeft * scaleFactor;
						obj.top = originalTop * scaleFactor;
						
						// For groups, Fabric.js automatically scales nested objects when we scale the group
						// But we need to ensure the group's bounding box is recalculated
						if (obj.type === 'group') {
							// Force group to recalculate its dimensions
							if (obj.setCoords) obj.setCoords();
							// Also ensure nested objects have their coords updated
							if (obj._objects && Array.isArray(obj._objects)) {
								obj._objects.forEach(nestedObj => {
									if (nestedObj && nestedObj.setCoords) {
										nestedObj.setCoords();
									}
								});
							}
						}
						
						// Update font size for text objects
						if (obj.type === 'textbox' || obj.type === 'i-text' || obj.type === 'text') {
							if (obj.fontSize) {
								obj.fontSize = (obj.fontSize || 12) * scaleFactor;
							}
						}
						
						// Ensure object is visible after scaling
						obj.opacity = 1;
						obj.visible = true;
						obj.selectable = true;
						
						// Update coordinates for all objects
						if (obj.setCoords) obj.setCoords();
					});
					
					// Recalculate canvas offsets after scaling
					if (fabcanvas.calcOffset) fabcanvas.calcOffset();
					fabcanvas.renderAll();
					fabcanvas.requestRenderAll();
				} else {
					// Even if no scaling needed, ensure all objects are visible
					objects.forEach(obj => {
						if (obj) {
							obj.opacity = 1;
							obj.visible = true;
							obj.selectable = true;
						}
					});
				}
				
				// CRITICAL: Ensure canvas dimensions are correct after all scaling
				if (canvas) {
					canvas.setDimensions({
						width: BASE_WIDTH,
						height: BASE_HEIGHT
					});
					canvas.calcOffset && canvas.calcOffset();
				}
				
				// Sync scale ref so __updateCanvasDimensions(100) does not scale objects again
				if (window.__setCanvasScaleRef) {
					window.__setCanvasScaleRef(100);
				}
				// Now update canvas dimensions tracking (this syncs zoom variables but won't scale objects again)
				if (window.__updateCanvasDimensions) {
					window.__updateCanvasDimensions(100);
				}
				// Clamp objects to canvas after load to prevent products going out of canvas
				clampAllObjectsToCanvas();
				
				fabcanvas.renderAll();
				fabcanvas.requestRenderAll();
				
				// CRITICAL: Update FabricCanvas state tracking to prevent it from reloading and overwriting scaled objects
				// This ensures FabricCanvas knows the state is already loaded and scaled, so it won't reload
				// Use the same JSON string that was used for tracking before loading
				if (window.__updateCanvasStateTracking && canvasStateForLoad) {
					try {
						// Create tracking version without non-Fabric properties (same as before loading)
						const canvasStateForTracking = { ...canvasStateForLoad };
						if (canvasStateForTracking.canvas_dimensions) {
							delete canvasStateForTracking.canvas_dimensions;
						}
						if (canvasStateForTracking.page_title) {
							delete canvasStateForTracking.page_title;
						}
						if (canvasStateForTracking.product_ids) {
							delete canvasStateForTracking.product_ids;
						}
						const canvasStateJson = JSON.stringify(canvasStateForTracking);
						const objectsOnlyJson = JSON.stringify(canvasStateForTracking.objects || []);
						window.__updateCanvasStateTracking(canvasStateJson, objectsOnlyJson);
					} catch (e) {
						// Ignore errors updating state tracking
					}
				}
				
				// CRITICAL: Final check - ensure all objects are visible and on canvas
				const finalObjects = fabcanvas.getObjects();
				finalObjects.forEach(obj => {
					if (obj) {
						obj.opacity = 1;
						obj.visible = true;
						obj.selectable = true;
						// Ensure object coordinates are updated
						if (obj.setCoords) obj.setCoords();
					}
				});
				
				fabcanvas.renderAll();
				fabcanvas.requestRenderAll();
				
				const newObjects = fabcanvas.getObjects().filter(o => o.skutxt);
				const productsOnNewPage = new Set();
				newObjects.forEach(obj => {
					if (obj.skutxt) {
						productsOnNewPage.add(obj.skutxt);
						if (getApplyToSelector && getApplyToSelector()) store.dispatch(hideProductInSelector(obj.skutxt));
					}
				});
				// Create ID mappings with awareness of current mode
				// Check the mode explicitly
				const inBuyPlanMode = detectBuyPlanMode();
				
				const idMap = new Map();
				if (window.buyplanSummaryData) {
					try {
						const buyplanItems = window.buyplanSummaryData.get('buyplan_items');
						if (buyplanItems) {
							for (let buyplanItem of buyplanItems.values()) {
								buyplanItem.get("items").forEach((item) => {
									const buyplanItemId = String(item.get("id"));
									const productId = String(item.get("product_id"));
									
									// Always store mapping in the forward direction
									idMap.set(buyplanItemId, productId);
									
									// Only create reverse mapping in buy plan mode
									if (inBuyPlanMode) {
										idMap.set(productId, buyplanItemId);
									}
								});
							}
						}
					} catch (e) {
						// Ignore mapping errors
					}
				}
				
				productsOnCanvas.forEach(skutxt => {
					if (!productsOnNewPage.has(skutxt)) {
						// Show the primary ID
						if (getApplyToSelector && getApplyToSelector()) {
							store.dispatch(showProductInSelector(skutxt));
						}
						
						// Also show the alternate ID if mapping exists - no mode check needed
						if (idMap.has(skutxt) && getApplyToSelector && getApplyToSelector()) {
							store.dispatch(showProductInSelector(idMap.get(skutxt)));
						}
					}
				});
				updateSelectionCanvasItems();
				// Force re-render of selector canvas after visibility is updated
				triggerProductGroupRerender();
			});
		});
	} else {
		productsOnCanvas.forEach(skutxt => { if (getApplyToSelector && getApplyToSelector()) store.dispatch(showProductInSelector(skutxt)); });
		updateSelectionCanvasItems();
		// Force re-render of selector canvas after visibility is updated
		triggerProductGroupRerender();
	}
	
	// CRITICAL FIX: After loading page state, we need to hide products that are on ANY page's canvas
	// This ensures that when navigating between pages, products already on canvas are properly hidden
	if (getApplyToSelector && getApplyToSelector()) {
		// Create ID mappings with explicit mode awareness
		// Always check the mode explicitly
		const inBuyPlanMode = detectBuyPlanMode();
		
		const idMap = new Map();
		if (window.buyplanSummaryData) {
			try {
				const buyplanItems = window.buyplanSummaryData.get('buyplan_items');
				if (buyplanItems) {
					for (let buyplanItem of buyplanItems.values()) {
						buyplanItem.get("items").forEach((item) => {
							const buyplanItemId = String(item.get("id"));
							const productId = String(item.get("product_id"));
							
							// Always map buyplan item ID to product ID
							idMap.set(buyplanItemId, productId);
							
							// Only map product ID to buyplan item ID in buy plan mode
							if (inBuyPlanMode) {
								idMap.set(productId, buyplanItemId);
							}
						});
					}
				}
			} catch (e) {
				// Ignore mapping errors
			}
		}
		
		// Get all products that are on any page's canvas from the canvas state all data
		const canvasStateAll = store.getState().get("canvasInfoReducer")?.getIn(["canvasStateAll", "data"]);
		if (canvasStateAll && canvasStateAll.get) {
			try {
				const canvasData = canvasStateAll.get("data");
				if (canvasData && canvasData.size > 0) {
					const monthData = canvasData.find(item => item.get("month") === getSelMonth());
					if (monthData) {
						const allProductsOnCanvas = monthData.get("products")?.toJS() || [];
						// Hide all products that are on any page's canvas
						allProductsOnCanvas.forEach(product => {
							if (product.id) {
								const productId = String(product.id);
								// Hide the primary ID
								store.dispatch(hideProductInSelector(productId));
								
								// Also hide the alternate ID if mapping exists - no mode check needed
								if (idMap.has(productId)) {
									store.dispatch(hideProductInSelector(idMap.get(productId)));
								}
							}
						});
					}
				}
			} catch (e) {
				// If there's an error accessing canvas state all, fall back to current behavior
			}
		}
	}
}

// Mark the canvas as saved (used by FabricCanvas after load/save)
export function markCanvasSaved() {
	try {
		if (!fabcanvas) return false;

		// Before marking as saved, ensure all products have correct visibility
		const objects = fabcanvas.getObjects();

		// We'll mark all objects as visible regardless of label changing state
		objects.forEach((obj) => {
			if (obj && obj.skutxt) {
				// Force all products to be visible when saving
				obj.opacity = 1;
				obj.selectable = true;
				obj.visible = true;

				// Mark all product objects as force visible
				obj.__forceVisible = true;
			}
		});

		fabcanvas.modified = false;

		// Trigger a selector canvas update after save is complete
		// This ensures products remain properly synced between canvas and selector
		setTimeout(() => {
			updateSelectionCanvasItems(true); // Force visibility
			// Force a render to ensure visibility changes are applied
			if (fabcanvas && typeof fabcanvas.renderAll === 'function') {
				fabcanvas.renderAll();
			}

			// Schedule another update to ensure visibility persists
			setTimeout(() => {
				updateSelectionCanvasItems(true);
				if (fabcanvas && typeof fabcanvas.renderAll === 'function') {
					fabcanvas.renderAll();
				}
			}, 1000);
		}, 200);

		return true;
	} catch (e) {
		return false;
	}
}

// Returns true if the main canvas has unsaved modifications
export function isMainCanvasModified() {
	try {
		if (!fabcanvas) return false;
		return !!fabcanvas.modified;
	} catch (e) {
		return false;
	}
}

// Initialize canvas event handlers (safe no-op if canvas not ready)
export function initCanvasEvents(onToolbarUpdate) {
	try {
		const canvas = getCanvas();
		if (!canvas) return;

		// ensure we don't attach duplicates
		if (canvas._helpersEventsAttached) return;
		canvas._helpersEventsAttached = true;

		const markModified = () => {
			try {
				canvas.modified = true;
				// Don't call onToolbarUpdate with empty object as it resets toolbar state
				// The toolbar state should be preserved during modifications
			} catch (e) {
				/* ignore */
			}
		};

		// Handle selection events to show/hide text size control
		const handleSelection = () => {
		  try {
		    const activeObject = canvas.getActiveObject();
		    
		    // Check if it's a text object
		    const isText = activeObject && (
		      activeObject.type === "textbox" ||
		      activeObject.type === "i-text" ||
		      activeObject.type === "text" ||
		      (activeObject.isType && activeObject.isType("textbox"))
		    );
		    
		    // Check if multiple objects are selected
		    const isMultiSelection = activeObject && activeObject.type === 'activeSelection' &&
		      activeObject._objects && activeObject._objects.length > 1;
		    
		    if (typeof onToolbarUpdate === 'function') {
		      onToolbarUpdate({
		        showFontSizeBtn: isText,
		        fontSize: isText ? (activeObject.fontSize || 12) : null,
		        multipleObjectsSelected: isMultiSelection
		      });
		    }
			} catch (e) {
				/* ignore */
			}
		};

		const handleDeselection = () => {
		  try {
		    if (typeof onToolbarUpdate === 'function') {
		      onToolbarUpdate({
		        showFontSizeBtn: false,
		        fontSize: null,
		        multipleObjectsSelected: false
		      });
		    }
			} catch (e) {
				/* ignore */
			}
		};

		// Constrain object movement to stay within canvas boundaries
		const constrainObjectMovement = (e) => {
			try {
				const obj = e.target;
				if (!obj || !canvas) {
					markModified();
					return;
				}

				// Update coordinates to get accurate bounding box (accounts for rotation, scaling, etc.)
				obj.setCoords();
				const bounds = obj.getBoundingRect();
				
				// Get object dimensions from bounding box (accounts for all transformations)
				const objWidth = bounds.width;
				const objHeight = bounds.height;
				const objLeft = bounds.left;
				const objTop = bounds.top;
				const objRight = objLeft + objWidth;
				const objBottom = objTop + objHeight;
				
				// Calculate how much the object needs to be shifted to stay within bounds
				let deltaX = 0;
				let deltaY = 0;
				
				// Constrain horizontal position - ensure entire object stays within canvas width
				if (objLeft < 0) {
					deltaX = -objLeft; // Move right to align left edge with canvas left edge
				} else if (objRight > canvas.width) {
					deltaX = canvas.width - objRight; // Move left to align right edge with canvas right edge
				}
				
				// Constrain vertical position - ensure entire object stays within canvas height
				if (objTop < 0) {
					deltaY = -objTop; // Move down to align top edge with canvas top edge
				} else if (objBottom > canvas.height) {
					deltaY = canvas.height - objBottom; // Move up to align bottom edge with canvas bottom edge
				}
				
				// Apply constraints if needed
				if (deltaX !== 0 || deltaY !== 0) {
					// Update object position (Fabric.js handles origin point correctly)
					obj.left += deltaX;
					obj.top += deltaY;
					// Update coordinates after position change
					obj.setCoords();
				}
				
				// Mark canvas as modified
				markModified();
			} catch (e) {
				// Ignore errors but still mark as modified to preserve existing behavior
				markModified();
			}
		};

		canvas.on && canvas.on('object:modified', markModified);
		canvas.on && canvas.on('object:added', markModified);
		canvas.on && canvas.on('object:removed', markModified);
		canvas.on && canvas.on('object:scaled', markModified);
		canvas.on && canvas.on('object:moving', constrainObjectMovement);
		
		// Add selection event handlers
		canvas.on && canvas.on('selection:created', handleSelection);
		canvas.on && canvas.on('selection:updated', handleSelection);
		canvas.on && canvas.on('selection:cleared', handleDeselection);
		
		// Initialize state - ensure text size control is hidden on startup
		handleDeselection();
	} catch (e) {
		/* ignore */
	}
}

/**
 * Clamp all canvas objects to stay within canvas bounds.
 * Call after zoom, load, or page change to prevent products drifting outside the canvas.
 */
export function clampAllObjectsToCanvas() {
	const canvas = getCanvas();
	if (!canvas) return;
	const objects = canvas.getObjects();
	if (!objects || objects.length === 0) return;
	objects.forEach((obj) => {
		if (!obj) return;
		try {
			obj.setCoords && obj.setCoords();
			const bounds = obj.getBoundingRect();
			const objLeft = bounds.left;
			const objTop = bounds.top;
			const objRight = objLeft + bounds.width;
			const objBottom = objTop + bounds.height;
			let deltaX = 0;
			let deltaY = 0;
			if (objLeft < 0) deltaX = -objLeft;
			else if (objRight > canvas.width) deltaX = canvas.width - objRight;
			if (objTop < 0) deltaY = -objTop;
			else if (objBottom > canvas.height) deltaY = canvas.height - objBottom;
			if (deltaX !== 0 || deltaY !== 0) {
				obj.left = (obj.left || 0) + deltaX;
				obj.top = (obj.top || 0) + deltaY;
				obj.setCoords && obj.setCoords();
			}
		} catch (e) {
			// ignore per-object errors
		}
	});
	canvas.requestRenderAll && canvas.requestRenderAll();
}

// Utility: validate a date string (used by BuyplanSummaryContainer)
export function isValidDate(d) {
	try {
		if (!d) return false;
		const dt = new Date(d);
		return !isNaN(dt.getTime());
	} catch (e) {
		return false;
	}
}

// Utility to calculate fitting dimensions for an object in a page area
export function calculateXYWidthHeight(objWidth, objHeight, maxWidth, maxHeight) {
	try {
		const wRatio = maxWidth / objWidth;
		const hRatio = maxHeight / objHeight;
		const ratio = Math.min(wRatio, hRatio, 1);
		return { width: Math.round(objWidth * ratio), height: Math.round(objHeight * ratio), ratio };
	} catch (e) {
		return { width: objWidth, height: objHeight, ratio: 1 };
	}
}

// Add 'Powered by' footer with optional logo to a jsPDF instance
export function addPoweredByText(pdf, xMargin, pageWidth, pageHeight, logoBaseImg) {
	try {
		if (!pdf) return;
		const text = 'Powered by Brandscope';
		const fontSize = 10;
		pdf.setFontSize(fontSize);
		pdf.setTextColor(115, 115, 115);
		const textWidth = pdf.getTextWidth(text || '');
		const x = pageWidth - xMargin - textWidth - 10;
		const y = pageHeight - xMargin / 2 - 15; // Increased spacing from bottom
		
		if (logoBaseImg) {
			try {
				const logoW = 20;
				const logoH = 20;
				// Position logo inline with text baseline - move logo up to align with text
				pdf.addImage(logoBaseImg, 'PNG', x - logoW - 6, y - logoH + 6, logoW, logoH);
			} catch (e) {
				// ignore logo add failures
			}
		}
		pdf.text(text, x, y);
	} catch (e) {
		// ignore
	}
}

// Add month title area on the page header (right aligned)
export function addMonthTitle(pdf, pageWidth, xMargin, yMargin, monthTitle) {
	try {
		if (!pdf) return;
		const title = monthTitle || '';
		const fontSize = 12;
		pdf.setFontSize(fontSize);
		pdf.setTextColor(115, 115, 115);
		const w = pdf.getTextWidth(title || '');
		const x = pageWidth - xMargin - w;
		const y = yMargin + 12;
		if (title) pdf.text(title, x, y);
	} catch (e) {
		// ignore
	}
}

// Add page number text at bottom-right
export function addPageNumber(pdf, xMargin, pageWidth, pageHeight, text) {
	try {
		if (!pdf) return;
		const txt = text || '';
		const fontSize = 9;
		pdf.setFontSize(fontSize);
		pdf.setTextColor(115, 115, 115);
		const w = pdf.getTextWidth(txt || '');
		const x = pageWidth - xMargin - w;
		const y = pageHeight - xMargin / 2;
		if (txt) pdf.text(txt, x, y);
	} catch (e) {
		// ignore
	}
}

// Set an active style on the currently selected object(s)
export function setActiveStyle(key, value) {
	try {
		const canvas = getCanvas();
		if (!canvas) return;
		const active = canvas.getActiveObject && canvas.getActiveObject();
		if (!active) return;
		try {
			active.set(key, value);
			if (active.setCoords) active.setCoords();
			if (canvas.requestRenderAll) canvas.requestRenderAll();
			saveCanvasState();
		} catch (e) {
			// ignore set failures
		}
	} catch (e) {
		// ignore
	}
}

// Add simple vertical/horizontal lines and a line (arrow placeholder) to the canvas
export function addVLine() {
	try {
		const canvas = getCanvas();
		if (!canvas) return;
		
		// Ensure offsets are recalculated before adding line (accounts for browser zoom)
		canvas.calcOffset();
		
		// Get current canvas scale percentage (defaults to 100% if not available)
		const currentScalePercent = (window.__getCurrentCanvasScale && window.__getCurrentCanvasScale()) || 100;
		const scaleFactor = currentScalePercent / 100; // Convert percentage to scale factor (e.g., 25% = 0.25)
		
		const line = new fabric.Line([0, -100 * scaleFactor, 0, 100 * scaleFactor], {
			left: canvas.width / 2,
			top: canvas.height / 2,
			stroke: '#000000',
			// Match old behavior: allow stroke to scale with object for "line width" changes.
			strokeWidth: 1 * scaleFactor,
			selectable: true,
		});
		canvas.add(line);
		canvas.setActiveObject(line);
		canvas.requestRenderAll && canvas.requestRenderAll();
		saveCanvasState();
	} catch (e) {
		// ignore
	}
}

export function addHLine() {
	try {
		const canvas = getCanvas();
		if (!canvas) return;
		
		// Ensure offsets are recalculated before adding line (accounts for browser zoom)
		canvas.calcOffset();
		
		// Get current canvas scale percentage (defaults to 100% if not available)
		const currentScalePercent = (window.__getCurrentCanvasScale && window.__getCurrentCanvasScale()) || 100;
		const scaleFactor = currentScalePercent / 100; // Convert percentage to scale factor (e.g., 25% = 0.25)
		
		const line = new fabric.Line([-100 * scaleFactor, 0, 100 * scaleFactor, 0], {
			left: canvas.width / 2,
			top: canvas.height / 2,
			stroke: '#000000',
			// Match old behavior: allow stroke to scale with object for "line width" changes.
			strokeWidth: 1 * scaleFactor,
			selectable: true,
		});
		canvas.add(line);
		canvas.setActiveObject(line);
		canvas.requestRenderAll && canvas.requestRenderAll();
		saveCanvasState();
	} catch (e) {
		// ignore
	}
}

export function addLineArrow() {
	try {
		const canvas = getCanvas();
		if (!canvas) return;
		
		// Ensure offsets are recalculated before adding line arrow (accounts for browser zoom)
		canvas.calcOffset();
		
		// Get current canvas scale percentage (defaults to 100% if not available)
		const currentScalePercent = (window.__getCurrentCanvasScale && window.__getCurrentCanvasScale()) || 100;
		const scaleFactor = currentScalePercent / 100; // Convert percentage to scale factor (e.g., 25% = 0.25)
		
		// Create a horizontal line with consistent stroke width
		const line = new fabric.Line([0, 0, 95 * scaleFactor, 0], {
			stroke: '#000000',
			// Match old behavior: allow stroke to scale with object for "line width" changes.
			strokeWidth: 3 * scaleFactor,
			originX: 'left',
			originY: 'center'
		});

		// Create arrowhead using a polygon for better control
		const arrowhead = new fabric.Polygon([
			{x: 120 * scaleFactor, y: 0},    // tip of arrow (extended further right)
			{x: 95 * scaleFactor, y: -10 * scaleFactor},   // top back point (wider base)
			{x: 95 * scaleFactor, y: 10 * scaleFactor}     // bottom back point (wider base)
		], {
			fill: '#000000',
			originX: 'left',
			originY: 'center'
		});

		// Group the line and arrowhead together
		const linearrowObj = new fabric.Group([line, arrowhead], {
			originX: 'center',
			originY: 'center',
			padding: 20 * scaleFactor // Scale padding
		});

		// Recalculate offsets before positioning (accounts for browser zoom)
		canvas.calcOffset();
		
		// Position the group in the center of the canvas
		linearrowObj.left = canvas.width / 2;
		linearrowObj.top = canvas.height / 2;
		linearrowObj.setCoords();

		canvas.add(linearrowObj);
		
		// Recalculate offsets again after adding
		canvas.calcOffset();
		canvas.setActiveObject(linearrowObj);
		canvas.requestRenderAll && canvas.requestRenderAll();
		saveCanvasState();
	} catch (e) {
		// ignore
	}
}

/**
 * Aligns multiple selected objects based on the first selected object
 * @param {string} direction - The direction to align objects ('left', 'right', 'centerX', 'top', 'bottom', 'centerY')
 * @returns {boolean} - Whether the alignment was successful
 */
export function alignObjects(direction) {
  try {
    const canvas = getCanvas();
    if (!canvas) return false;

    const activeObject = canvas.getActiveObject();
    if (!activeObject) return false;

    // Get all active objects (works for both single selection and activeSelection)
    const active = canvas.getActiveObjects();
    if (!active || active.length < 2) return false;

    // Store if we have an activeSelection to restore it later
    const isActiveSelection = activeObject && activeObject.type === 'activeSelection';

    // First, ensure all objects have updated coordinates
    active.forEach(obj => {
      obj.setCoords();
    });

    let ref;
    let refValue;

    // Find the reference value based on direction
    if (direction === 'left') {
      // Find the leftmost object
      ref = active.reduce((leftmost, obj) => {
        const bounds = obj.getBoundingRect();
        const leftmostBounds = leftmost.getBoundingRect();
        return bounds.left < leftmostBounds.left ? obj : leftmost;
      });
      const refBounds = ref.getBoundingRect();
      refValue = refBounds.left;
    } else if (direction === 'top') {
      // Find the topmost object
      ref = active.reduce((topmost, obj) => {
        const bounds = obj.getBoundingRect();
        const topmostBounds = topmost.getBoundingRect();
        return bounds.top < topmostBounds.top ? obj : topmost;
      });
      const refBounds = ref.getBoundingRect();
      refValue = refBounds.top;
    } else if (direction === 'right') {
      // Find the rightmost object
      ref = active.reduce((rightmost, obj) => {
        const bounds = obj.getBoundingRect();
        const rightmostBounds = rightmost.getBoundingRect();
        return (bounds.left + bounds.width) > (rightmostBounds.left + rightmostBounds.width) ? obj : rightmost;
      });
      const refBounds = ref.getBoundingRect();
      refValue = refBounds.left + refBounds.width;
    } else if (direction === 'bottom') {
      // Find the bottommost object
      ref = active.reduce((bottommost, obj) => {
        const bounds = obj.getBoundingRect();
        const bottommostBounds = bottommost.getBoundingRect();
        return (bounds.top + bounds.height) > (bottommostBounds.top + bottommostBounds.height) ? obj : bottommost;
      });
      const refBounds = ref.getBoundingRect();
      refValue = refBounds.top + refBounds.height;
    } else if (direction === 'centerX') {
      // Use first object's center as reference
      ref = active[0];
      const refBounds = ref.getBoundingRect();
      refValue = refBounds.left + refBounds.width / 2;
    } else if (direction === 'centerY') {
      // Use first object's center as reference
      ref = active[0];
      const refCenter = ref.getCenterPoint();
      refValue = refCenter.y;
    }

    // Align each object based on the specified direction
    active.forEach(obj => {
      if (obj === ref) return; // Skip the reference object
      
      obj.setCoords();
      const bounds = obj.getBoundingRect();

      switch (direction) {
        case 'left':
          // Align left edges
          const leftDiff = refValue - bounds.left;
          obj.left = obj.left + leftDiff;
          break;
        case 'right':
          // Align right edges
          const objRight = bounds.left + bounds.width;
          const rightDiff = refValue - objRight;
          obj.left = obj.left + rightDiff;
          break;
        case 'centerX':
          // Align centers horizontally
          const objCenterX = bounds.left + bounds.width / 2;
          const centerXDiff = refValue - objCenterX;
          obj.left = obj.left + centerXDiff;
          break;
        case 'top':
          // Align top edges
          const topDiff = refValue - bounds.top;
          obj.top = obj.top + topDiff;
          break;
        case 'bottom':
          // Align bottom edges
          const objBottom = bounds.top + bounds.height;
          const bottomDiff = refValue - objBottom;
          obj.top = obj.top + bottomDiff;
          break;
        case 'centerY':
          // Align centers vertically
          const objCenter = obj.getCenterPoint();
          const centerYDiff = refValue - objCenter.y;
          obj.top = obj.top + centerYDiff;
          break;
      }

      // Update object coordinates after position change
      obj.setCoords();
    });

    // If we had an activeSelection, recreate it with correct bounding box
    if (isActiveSelection) {
      // Discard the old selection first
      canvas.discardActiveObject();
      
      // Ensure all objects have final updated coordinates
      active.forEach(obj => {
        obj.setCoords();
      });
      
      // Recreate the activeSelection - Fabric.js will automatically calculate
      // the bounding box from topmost, leftmost, rightmost, bottommost products
      const newActiveSelection = new fabric.ActiveSelection(active, {
        canvas: canvas
      });
      
      // Set the new activeSelection
      canvas.setActiveObject(newActiveSelection);
      newActiveSelection.setCoords();
    }

    // Update canvas
    canvas.renderAll();
    
    // Save the canvas state after alignment
    saveCanvasState();
    
    return true;
  } catch (e) {
    // Silently handle errors
    return false;
  }
}
